import {
  Button,
  ColumnTable,
  Modal,
  OutlinedSelect,
  OutlinedSelectOptionType,
  Table,
  addToast,
} from '@octano/global-ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import Download from '../../../../../AcademicRecord/parts/Download';
import {
  CurrencyOverviewItem,
  CurrencyYearValue,
  exportCurrencyRatesByYear,
  getCurrencyRateYears,
  getCurrencyRatesByYear,
} from '../api';
import styles from './RateHistoryModal.module.scss';

const dictPrefix = 'financialPortal.settings.currencies.history';
const MONTHS_IN_YEAR = 12;
const DAYS_IN_MATRIX = 31;

interface Props {
  currency: CurrencyOverviewItem | null;
  onClose: () => void;
}

interface MatrixRow {
  day: number;
  values: (number | null)[];
}

/**
 * Indexa el listado plano `{ date, value }[]` en una matriz día x mes.
 * `date` se parsea con `slice` sobre el string `YYYY-MM-DD`, nunca con
 * `new Date(dateString)` + getters locales: eso desfasa el día en husos
 * horarios de offset negativo (bug ya visto en el trabajo de sync).
 */
function buildMatrix(values: CurrencyYearValue[]): MatrixRow[] {
  const byMonth: (number | null)[][] = Array.from({ length: MONTHS_IN_YEAR }, () =>
    Array(DAYS_IN_MATRIX + 1).fill(null),
  );
  values.forEach(({ date, value }) => {
    const month = Number(date.slice(5, 7));
    const day = Number(date.slice(8, 10));
    if (month >= 1 && month <= MONTHS_IN_YEAR && day >= 1 && day <= DAYS_IN_MATRIX) {
      byMonth[month - 1][day] = value;
    }
  });

  return Array.from({ length: DAYS_IN_MATRIX }, (_, index) => {
    const day = index + 1;
    return {
      day,
      values: byMonth.map((month) => month[day]),
    };
  });
}

export function RateHistoryModal({ currency, onClose }: Props) {
  const { t } = useTranslation();
  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [values, setValues] = useState<CurrencyYearValue[]>([]);
  const [isLoadingYears, setIsLoadingYears] = useState(false);
  const [isLoadingValues, setIsLoadingValues] = useState(false);

  // Se dispara en cada cambio de `currency` — abrir, cerrar (-> null) o pasar
  // directo de una moneda a otra. Limpia el estado antes de pedir los años
  // nuevos para que no se alcance a ver la matriz de la moneda anterior.
  useEffect(() => {
    setYears([]);
    setSelectedYear(null);
    setValues([]);
    if (!currency) {
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoadingYears(true);
      const { data, error } = await getCurrencyRateYears(currency.id);
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setYears(data.years);
        // El endpoint entrega los años más nuevos primero.
        setSelectedYear(data.years[0] ?? null);
      }
      setIsLoadingYears(false);
    })();
    return () => {
      cancelled = true;
    };
    // `t` queda fuera a propósito: react-i18next no garantiza su identidad
    // entre renders y volvería a disparar este efecto en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  useEffect(() => {
    if (!currency || !selectedYear) {
      setValues([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoadingValues(true);
      const { data, error } = await getCurrencyRatesByYear(currency.id, selectedYear);
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setValues(data.values);
      }
      setIsLoadingValues(false);
    })();
    return () => {
      cancelled = true;
    };
    // `t` queda fuera a propósito, ver nota en el efecto anterior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, selectedYear]);

  const yearOptions = useMemo<OutlinedSelectOptionType[]>(
    () => years.map((year) => ({ label: String(year), value: year })),
    [years],
  );

  const selectedYearOption = useMemo(
    () => yearOptions.find((option) => option.value === selectedYear),
    [yearOptions, selectedYear],
  );

  const rows = useMemo<MatrixRow[]>(() => buildMatrix(values), [values]);

  // Typed via the `useMemo` generic (as in useDebtPublicationColumns.tsx)
  // rather than annotating each `cellFormat`'s destructured param.
  const columns = useMemo<ColumnTable<MatrixRow>[]>(() => {
    const months = t(`${dictPrefix}.months`, { returnObjects: true }) as string[];

    const dayColumn: ColumnTable<MatrixRow> = {
      columnName: 'day',
      headerText: t(`${dictPrefix}.day`),
      thClassName: 'text-center',
      tdClassName: 'text-center',
      width: 60,
    };

    const monthColumns: ColumnTable<MatrixRow>[] = months.map((label, index) => ({
      columnName: `month-${index}`,
      headerText: label,
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        const value = row.values[index];
        return value === null
          ? ''
          : value.toLocaleString('es-CL', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
      },
    }));

    return [dayColumn, ...monthColumns];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return (
    <Modal isOpen={Boolean(currency)} toggle={onClose} size="xl">
      <h1 className="text-dark fs-22 mb-4">
        {t(`${dictPrefix}.title`, { code: currency?.code ?? '' })}
      </h1>
      <Row className="align-items-end mb-3">
        <Col xs={12} md={4} className={styles.yearSelect}>
          <OutlinedSelect
            name="year"
            label={t(`${dictPrefix}.year`)}
            value={selectedYearOption}
            options={yearOptions}
            isClearable={false}
            disabled={isLoadingYears || yearOptions.length === 0}
            onChange={(option: OutlinedSelectOptionType) =>
              setSelectedYear(Number(option.value))
            }
          />
        </Col>
        <Col xs={12} md={8} className="d-flex justify-content-end">
          <Download
            filename={`Historial_${currency?.code ?? ''}_${selectedYear ?? ''}`}
            request={() => exportCurrencyRatesByYear(currency!.id, selectedYear!)}
            disabled={!currency || !selectedYear}
            texts={{
              action: t(`${dictPrefix}.download`),
              error: t(`${dictPrefix}.loadError`),
            }}
          />
        </Col>
      </Row>
      <div className={styles.rateHistoryTable}>
        <Table
          columns={columns}
          data={rows}
          isLoadingResults={isLoadingValues}
          borderless={false}
          striped={false}
          height={420}
        />
      </div>
      <Row className="mt-4">
        <Col className="d-flex justify-content-end">
          <Button text={t(`${dictPrefix}.close`)} onClick={onClose} />
        </Col>
      </Row>
    </Modal>
  );
}
